
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import ThankYouLoadingState from '@/components/ThankYou/ThankYouLoadingState';
import ThankYouErrorState from '@/components/ThankYou/ThankYouErrorState';
import ThankYouContentCard from '@/components/ThankYou/ThankYouContentCard';
import { fetchOrderDetails, sendOrderParamsToBackend } from '@/services/orderService';
import { supabase } from '@/integrations/supabase/client';
import { fetchPlanById } from '@/services/plansService';

const ThankYouPage = () => {
  const location = useLocation();
  const [planName, setPlanName] = useState<string>('');
  const [planData, setPlanData] = useState<any>(null);
  const [email, setEmail] = useState<string>('');
  const [loaded, setLoaded] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [orderId, setOrderId] = useState<string>('');
  const [retryCount, setRetryCount] = useState<number>(0);
  const [initialProcessingDone, setInitialProcessingDone] = useState<boolean>(false);
  const [isDirect, setIsDirect] = useState<boolean>(false);
  
  // Track CompleteRegistration event when the thank you page loads
  useEffect(() => {
    if (typeof fbq !== 'undefined') {
      console.log('Tracking CompleteRegistration event on thank you page');
      fbq('track', 'CompleteRegistration');
    } else {
      console.warn('Facebook Pixel not available');
    }
  }, []);
  
  useEffect(() => {
    const loadOrderDetails = async () => {
      // Only run this effect once
      if (initialProcessingDone) return;
      
      try {
        // Extract order details from URL and session storage
        const searchParams = new URLSearchParams(location.search);
        const orderIdFromUrl = searchParams.get('order_id');
        const orderStatus = searchParams.get('order_status');
        const planFromUrl = searchParams.get('plan');
        
        // Get stored values from session storage as fallbacks
        const storedOrderId = sessionStorage.getItem('last_order_id');
        const storedPlan = sessionStorage.getItem('purchased_plan');
        const storedEmail = sessionStorage.getItem('customer_email');
        
        console.log('Order details from URL:', { orderIdFromUrl, orderStatus, planFromUrl });
        console.log('Order details from session storage:', { storedOrderId, storedPlan, storedEmail });
        
        // Check if this is a direct purchase (bypass payment gateway)
        const isDirectPurchase = orderStatus === 'DIRECT';
        setIsDirect(isDirectPurchase);
        
        if (isDirectPurchase) {
          console.log('Processing direct purchase without payment gateway');
        }
        
        // Set order ID - prefer URL value over stored value
        const actualOrderId = orderIdFromUrl || storedOrderId;
        setOrderId(actualOrderId || '');
        
        // Set email from session storage if available
        if (storedEmail) {
          setEmail(storedEmail);
          console.log(`Using email from session storage: ${storedEmail}`);
        }
        
        // Set plan name - prefer URL value over stored value
        let derivedPlanName = '';
        let planId = planFromUrl || storedPlan || 'basic';
        
        // Try to fetch plan data from the plans service
        try {
          const planDetails = await fetchPlanById(planId);
          if (planDetails) {
            setPlanData(planDetails);
            derivedPlanName = `${planDetails.title} Plan`;
            setPlanName(derivedPlanName);
            console.log(`Using plan data from service: ${derivedPlanName}`, planDetails);
          } else {
            // Fallback if plan not found
            derivedPlanName = planId.charAt(0).toUpperCase() + planId.slice(1) + ' Plan';
            setPlanName(derivedPlanName);
            console.log(`Using fallback plan name: ${derivedPlanName}`);
          }
        } catch (error) {
          console.error('Error fetching plan data:', error);
          // Fallback plan name
          derivedPlanName = planId.charAt(0).toUpperCase() + planId.slice(1) + ' Plan';
          setPlanName(derivedPlanName);
        }
          
        // Process URL parameters if we have them
        if (planFromUrl && orderIdFromUrl) {
          try {
            console.log('Sending URL parameters to backend for storage');
            await sendOrderParamsToBackend({
              order_id: orderIdFromUrl,
              plan: planFromUrl,
              status: isDirectPurchase ? 'DEMO_PURCHASE' : (orderStatus || 'PENDING'),
              email: storedEmail || ''
            });
              
            // For direct purchases, invoke the payment check function directly
            // Profile creation will happen in the webhook handler after payment confirmation
            if (isDirectPurchase && storedEmail) {
              try {
                console.log(`Direct purchase: Invoking payment status check for order ${orderIdFromUrl}`);
                await supabase.functions.invoke('create-payment-order', {
                  body: {
                    checkPaymentStatus: true,
                    orderId: orderIdFromUrl,
                    isDirectPurchase: true,
                    userEmail: storedEmail,
                    planId: planId,
                    fromThankYouPage: true
                  }
                });
              } catch (profileError) {
                console.error('Error processing direct purchase:', profileError);
              }
            }
              
            // Initial payment status check for non-direct purchases
            if (actualOrderId && !isDirectPurchase) {
              try {
                console.log('Checking payment status with backend function');
                await supabase.functions.invoke('create-payment-order', {
                  body: {
                    checkPaymentStatus: true,
                    orderId: actualOrderId,
                    fromThankYouPage: true
                  }
                });
              } catch (err) {
                console.error('Error checking payment status:', err);
              }
            }
          } catch (err) {
            console.error('Error sending URL parameters to backend:', err);
          }
        }
        
        // If we have an order ID and don't have complete details, try to fetch them
        if (actualOrderId && (!email || !derivedPlanName) && retryCount < 2) {
          console.log('Making attempt to fetch order details for ID:', actualOrderId, 'attempt:', retryCount + 1);
          setRetryCount(prev => prev + 1);
          
          try {
            // Try direct database query first
            const { data: orderData, error: dbError } = await supabase
              .from('payment_orders')
              .select('*')
              .eq('order_id', actualOrderId)
              .maybeSingle();
            
            if (dbError) {
              console.error('Error querying database directly:', dbError);
            } else if (orderData) {
              console.log('Successfully fetched order details from database:', orderData);
              
              // Set email from order data if needed
              if (!email && orderData.user_email) {
                setEmail(orderData.user_email);
                sessionStorage.setItem('customer_email', orderData.user_email);
                console.log(`Setting email to "${orderData.user_email}" from database`);
              }
              
              // Set plan name if needed
              if (orderData.product_name) {
                setPlanName(orderData.product_name);
                console.log(`Setting plan name to "${orderData.product_name}" from database`);
                
                // Try to get plan ID from the product name
                if (orderData.payment_details && 
                    typeof orderData.payment_details === 'object' && 
                    orderData.payment_details !== null) {
                  
                  // Type-safe access to nested properties
                  const paymentDetails = orderData.payment_details as Record<string, any>;
                  const linkMeta = paymentDetails.link_meta as Record<string, any> | undefined;
                  
                  if (linkMeta && linkMeta.plan) {
                    const planIdFromMeta = linkMeta.plan;
                    try {
                      const planDetails = await fetchPlanById(planIdFromMeta);
                      if (planDetails) {
                        setPlanData(planDetails);
                        console.log(`Found plan details for ID ${planIdFromMeta}:`, planDetails);
                      }
                    } catch (error) {
                      console.error(`Error fetching plan details for ID ${planIdFromMeta}:`, error);
                    }
                  }
                }
              }
            } else if (retryCount < 1) {
              // One more retry after a delay if we still don't have the data
              setTimeout(() => {
                setRetryCount(prev => prev + 1);
              }, 2000);
              return; // Exit early to prevent setting loaded state
            }
          } catch (err) {
            console.error('Error fetching order details:', err);
          }
        }
        
        // Use fallbacks if we still don't have complete information
        if (!email && storedEmail) {
          console.log(`Using fallback email: ${storedEmail}`);
          setEmail(storedEmail);
        }
        
        if (!planName && retryCount >= 1) {
          console.log('Using default plan name: "Premium Subscription"');
          setPlanName("Premium Subscription");
        }
        
        setInitialProcessingDone(true);
        setLoaded(true);
      } catch (error) {
        console.error("Error loading order details:", error);
        setError("Failed to load order details. Please contact support.");
        setLoaded(true);
        toast.error("Error loading order details");
      }
    };
    
    loadOrderDetails();
  }, [location.search, retryCount, initialProcessingDone]);
  
  if (!loaded) {
    return <ThankYouLoadingState />;
  }
  
  if (error) {
    return <ThankYouErrorState errorMessage={error} />;
  }
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-krix-purple/5 to-transparent p-4">
      <ThankYouContentCard 
        planName={planName}
        planData={planData}
        email={email}
        orderId={orderId}
        isDirect={isDirect}
      />
    </div>
  );
};

export default ThankYouPage;
